import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { SelectOptionProps } from '@/models/SelectOption';
import { UserProps, UserType, userTypeAsString } from '@/models/User';

type EditUserModalProps = {
  open: boolean;
  onClose: () => void;

  user?: UserProps | null;

  // Called when user clicks "Save"
  onSave: (updatedUser: UserProps) => Promise<void>;

  saving?: boolean;
};

export default function EditUserModal({ open, onClose, user, onSave, saving }: EditUserModalProps) {
  const initialUserTypeId = useMemo(() => {
    return user?.userType ?? '';
  }, [user]);

  const [selectedUserTypeId, setSelectedUserTypeId] = useState<number | string>('');

  const userTypeOptions: SelectOptionProps[] = useMemo(() => {
    return (
      Object.values(UserType)
        // TypeScript enums include both keys + values → filter numbers only
        .filter((v): v is UserType => typeof v === 'number')
        .map((ut) => ({
          value: ut,
          label: userTypeAsString(ut),
        }))
    );
  }, []);

  useEffect(() => {
    setSelectedUserTypeId(initialUserTypeId);
  }, [initialUserTypeId, open]);

  const canSave =
    !!user?.b2CId &&
    selectedUserTypeId !== '' &&
    selectedUserTypeId !== initialUserTypeId &&
    !saving;

  const handleSave = async () => {
    if (!user?.b2CId || selectedUserTypeId === '') return;

    const updatedUser: UserProps = {
      ...user,
      userType: selectedUserTypeId as UserType,
    };

    await onSave(updatedUser);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithClose onClose={onClose}>Edit User</DialogTitleWithClose>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='overline2'>User</Typography>
          <Typography color='textSecondary'>
            {user
              ? `${user?.contact?.firstName ?? ''} ${user?.contact?.lastName ?? ''} (${user?.email ?? ''})`
              : '—'}
          </Typography>
        </Box>

        <TextField
          fullWidth
          size='small'
          label='User Type'
          select
          value={selectedUserTypeId}
          onChange={(e) => setSelectedUserTypeId(e.target.value)}
        >
          {/* Optional placeholder */}
          <MenuItem value='' disabled>
            Select a user type…
          </MenuItem>

          {userTypeOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      <DialogActions>
        <Button variant='secondary' size='large' onClick={onClose}>
          Cancel
        </Button>

        <Button variant='primary' size='large' onClick={handleSave} disabled={!canSave}>
          {saving ? (
            <>
              <span>Saving… </span>
              <CircularProgress size='16px' sx={{ ml: 1 }} />
            </>
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
